/**
 * QC Return for Correction Modal (Phase 1.5 UX — Feb 2026)
 *
 * Layout goals (per stakeholder UX feedback):
 *   • Compact header with order context inline (Order #, Product, Submitter)
 *   • Replace the bulky yellow info panel with a single status pill
 *   • Two-column top row (Error Category × Severity) saves vertical space
 *   • Quick-issue templates pre-populate Correction Notes
 *   • Sticky footer keeps Cancel + Return for Correction always visible
 *   • Scrollable body bounded by 90vh so footer never gets pushed off
 *
 * Backend contract (POST /api/orders/{id}/qc-return):
 *   rejection_category : QCRejectionCategory  (required)
 *   error_type         : str | null           (optional)
 *   rejection_reason   : str                  (required, 1-2000)
 *   evidence_file_ids  : list[str]            (optional, max 5)
 *   severity           : "minor"|"major"|"critical" | null  (optional, new)
 *
 * Internal DB status remains `pending_correction`. User-facing label is
 * always "Correction Required" everywhere in the app.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  RotateCcw, X, ChevronDown, Paperclip, Upload,
  Sparkles, ShieldAlert, ShieldCheck, Flame,
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { isTaxProduct } from '../../utils/orderHelpers';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ERROR_CATEGORIES = [
  { value: 'data_error',          label: 'Data Error' },
  { value: 'missing_info',        label: 'Missing Info' },
  { value: 'document_issue',      label: 'Document Issue' },
  { value: 'calculation_issue',   label: 'Calculation Issue' },
  { value: 'wrong_jurisdiction',  label: 'Wrong Jurisdiction' },
];

const SEVERITIES = [
  { value: 'minor',    label: 'Minor',    icon: ShieldCheck, activeCls: 'bg-blue-600 text-white border-blue-600',     idleCls: 'text-blue-700 border-blue-200 hover:bg-blue-50' },
  { value: 'major',    label: 'Major',    icon: ShieldAlert, activeCls: 'bg-amber-600 text-white border-amber-600',   idleCls: 'text-amber-700 border-amber-200 hover:bg-amber-50' },
  { value: 'critical', label: 'Critical', icon: Flame,       activeCls: 'bg-red-600 text-white border-red-600',       idleCls: 'text-red-700 border-red-200 hover:bg-red-50' },
];

const QUICK_TEMPLATES = [
  { label: 'Wrong Parcel',                   notes: 'The parcel number(s) on the certificate do not match the property of record. Please verify against the source and update before resubmitting.' },
  { label: 'Missing Tax Year',               notes: 'One or more required tax years are missing from the certificate. Please add the missing year(s) and confirm balances.' },
  { label: 'Incorrect Collector',            notes: 'The tax collector / payee name or address is incorrect. Please confirm against the official source and update accordingly.' },
  { label: 'Incorrect Payoff',               notes: 'The payoff amount(s) do not match the official statement. Please recalculate and correct each affected installment.' },
  { label: 'Missing Attachment',             notes: 'A required supporting document is missing. Please attach the missing file before resubmitting.' },
  { label: 'Narrative Error',                notes: 'The narrative contains incorrect or unclear information. Please revise so it accurately reflects the certificate findings.' },
  { label: 'Incorrect Tax Amounts',          notes: 'One or more tax amounts are incorrect. Please verify each installment against the official source and correct the figures.' },
  { label: 'Prior Taxes Not Confirmed',      notes: 'Prior-year tax status has not been confirmed. Please verify that prior taxes are paid (or call out any delinquencies) before resubmitting.' },
  { label: 'Local Entity Taxes Not Confirmed', notes: 'Local entity / municipal taxes have not been confirmed. Please verify all applicable local entities and document their status.' },
];

const QCReturnModal = ({ isOpen, onClose, onConfirm, order }) => {
  const [category, setCategory] = useState('');
  const [errorType, setErrorType] = useState('');
  const [severity, setSeverity] = useState(''); // mandatory — no preselection
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [evidence, setEvidence] = useState([]);
  const [uploading, setUploading] = useState(false);
  // MLS-DUAL-001 Phase 3: component-granular return for MLS dual orders
  const [affectedComponents, setAffectedComponents] = useState([]);
  const isMlsDual = ['MLS', 'MLS Update', 'Update MLS'].includes(order?.client_product)
    || !!order?.component_rollup || !!(order?.components && order.components.mls);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  // QC-RETURN-SIMPLIFY-001: Title Search QC gets a simplified form —
  // no Error Type / Quick Templates, evidence limited to 1 file / 2 MB.
  const isTitleQC = !isTaxProduct(order);
  const MAX_FILES = isTitleQC ? 1 : 5;
  const MAX_SIZE = (isTitleQC ? 2 : 10) * 1024 * 1024;
  const MAX_SIZE_LABEL = isTitleQC ? '2 MB' : '10 MB';

  useEffect(() => {
    const close = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setCategory(''); setErrorType(''); setSeverity('');
      // TYPING-RETURN-TO-QC-001 — pre-quote the Typing return so the Search Processor gets full context
      const typingReason = order?.typing_status === 'returned_to_qc' ? (order?.typing_return_reason || '').trim() : '';
      setNotes(typingReason ? `Typing return reason (${order?.typing_returned_by_name || 'Typing'}): "${typingReason}"\n\nQC notes: ` : '');
      setError(''); setEvidence([]);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen || !order) return null;

  /* ── Evidence upload ───────────────────────────────────────── */
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (evidence.length + files.length > MAX_FILES) {
      toast.error(MAX_FILES === 1
        ? 'Only 1 evidence file is allowed per return.'
        : `Maximum ${MAX_FILES} evidence files per return.`);
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      for (const file of files) {
        if (file.size > MAX_SIZE) { toast.error(`${file.name} exceeds the ${MAX_SIZE_LABEL} limit.`); continue; }
        const fd = new FormData();
        fd.append('file', file);
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const mime = (file.type || '').toLowerCase();
        let backendCategory = 'supporting_document';
        if (mime.startsWith('image/') || ext === 'png' || ext === 'jpg' || ext === 'jpeg' || ext === 'pdf' || mime === 'application/pdf') {
          backendCategory = 'image';
        } else if (ext === 'txt' || mime === 'text/plain') {
          backendCategory = 'internal_note';
        }
        fd.append('category', backendCategory);
        const res = await fetch(`${API}/orders/${order.id}/files/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (res.ok) {
          const body = await res.json();
          const fid = body.file_id || body.id || body.fileId;
          if (fid) {
            setEvidence(prev => [...prev, { file_id: fid, filename: file.name, size: file.size }]);
          } else {
            toast.error(`Upload succeeded but no file_id returned for ${file.name}`);
          }
        } else {
          const d = await res.json().catch(() => ({}));
          toast.error(`Upload failed: ${d.detail || file.name}`);
        }
      }
    } catch {
      toast.error('Network error during upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeEvidence = (fid) => setEvidence(prev => prev.filter(e => e.file_id !== fid));

  const applyTemplate = (tpl) => {
    setNotes(tpl.notes);
    setErrorType((prev) => prev || tpl.label);
    if (error) setError('');
  };

  const selectedLabel = ERROR_CATEGORIES.find(c => c.value === category)?.label || 'Select error category';
  const isValid = category && severity && notes.trim().length > 0 && (!isMlsDual || affectedComponents.length > 0);

  const handleConfirm = async () => {
    if (!category) { setError('Please select an error category.'); return; }
    if (!severity) {
      // QC-CONSOLIDATE-001 (Feb 2026): mirrors the backend 400 message.
      setError('Severity is required — choose Minor, Major, or Critical.');
      return;
    }
    if (!notes.trim()) { setError('Please describe what the processor must fix.'); return; }
    if (isMlsDual && affectedComponents.length === 0) {
      setError('Select the affected component(s) — Tax Search, MLS Search, or both.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      // QCInlineActions / QCDashboard onConfirm signature (kept stable):
      //   (category, errorType, notes, evidenceFileIds, severity, affectedComponents)
      await onConfirm(category, errorType.trim() || null, notes.trim(), evidence.map(e => e.file_id), severity,
        isMlsDual ? affectedComponents : null);
      setCategory(''); setErrorType(''); setSeverity(''); setNotes(''); setEvidence([]); setAffectedComponents([]);
    } catch (err) {
      setError(err.message || 'Failed to return order.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal shell: flex column so header / scroll body / footer split correctly */}
      <div
        className="relative bg-white rounded-lg shadow-xl w-full max-w-xl mx-4 flex flex-col max-h-[90vh] overflow-hidden"
        data-testid="qc-return-modal"
      >
        {/* ── Compact header with all order context inline ───────── */}
        <div className="flex items-start justify-between px-5 py-3 border-b border-amber-200 bg-amber-50 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-1.5 bg-amber-100 rounded-full flex-shrink-0">
              <RotateCcw className="w-4 h-4 text-amber-700" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 leading-tight">Return for Correction</h2>
              <p className="text-xs text-gray-600 mt-0.5 truncate">
                <span className="font-medium">{order.order_number}</span>
                {order.client_product && (
                  <> · <span className="text-gray-500">{order.client_product}</span></>
                )}
                {order.qc_submitted_by_name && (
                  <> · <span className="text-gray-500">by {order.qc_submitted_by_name}</span></>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 flex-shrink-0"
            data-testid="qc-return-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Scrollable body ─────────────────────────────────────── */}
        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">
          {/* Feb 2026 — Phase 1.5 UX iteration: status banner removed per
              stakeholder feedback. The "Correction Required" semantics are
              already implied by the modal title and the Return button, so
              the row of bullets was pure noise. */}

          {/* MLS-DUAL-001: affected component selector for MLS dual orders —
              ONLY the selected component(s) reopen for correction */}
          {isMlsDual && (
            <div className="mb-3" data-testid="qc-return-affected-components">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Affected Component(s) <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-4 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                {[['tax', 'Tax Search'], ['mls', 'MLS Search']].map(([val, label]) => (
                  <label key={val} className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={affectedComponents.includes(val)}
                      onChange={(e) => setAffectedComponents((prev) =>
                        e.target.checked ? [...prev, val] : prev.filter((c) => c !== val))}
                      className="w-4 h-4 accent-amber-600"
                      data-testid={`qc-return-component-${val}`}
                    />
                    {label}
                  </label>
                ))}
                <span className="text-[11px] text-gray-400 ml-auto">Only selected component(s) reopen — the other stays complete</span>
              </div>
            </div>
          )}

          {/* Row: Error Category × Severity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            {/* Error Category dropdown */}
            <div ref={dropdownRef}>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Error Category <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowDropdown(!showDropdown)}
                  className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg bg-white text-left text-sm ${!category && error ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:ring-2 focus:ring-amber-500`}
                  data-testid="qc-return-category"
                >
                  <span className={category ? 'text-gray-900' : 'text-gray-400'}>{selectedLabel}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showDropdown && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                    {ERROR_CATEGORIES.map(c => (
                      <button
                        key={c.value} type="button"
                        onClick={() => { setCategory(c.value); setShowDropdown(false); if (error) setError(''); }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${category === c.value ? 'bg-amber-50 text-amber-800 font-medium' : 'text-gray-700'}`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Severity segmented control — mandatory */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Severity <span className="text-red-500">*</span>
              </label>
              <div
                className={`flex gap-1.5 ${!severity && error ? 'ring-1 ring-red-300 rounded-md' : ''}`}
                data-testid="qc-return-severity"
              >
                {SEVERITIES.map(s => {
                  const Icon = s.icon;
                  const active = severity === s.value;
                  return (
                    <button
                      key={s.value} type="button"
                      onClick={() => { setSeverity(s.value); if (error) setError(''); }}
                      className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-2 text-xs font-medium rounded-md border transition-colors ${active ? s.activeCls : `bg-white ${s.idleCls}`}`}
                      data-testid={`qc-return-severity-${s.value}`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Error Type free-text — Tax QC only (removed for Title Search QC) */}
          {!isTitleQC && (
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Error Type <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text" value={errorType} onChange={e => setErrorType(e.target.value)}
                placeholder="e.g. Missing collector phone, typo in mailing address..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                maxLength={100} data-testid="qc-return-error-type"
              />
            </div>
          )}

          {/* Quick-issue templates — Tax QC only (removed for Title Search QC) */}
          {!isTitleQC && (
            <div className="mb-3" data-testid="qc-return-templates">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1.5">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                Quick Templates
                <span className="text-gray-400 font-normal">(optional — pre-fills notes)</span>
              </label>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_TEMPLATES.map(tpl => (
                  <button
                    key={tpl.label} type="button"
                    onClick={() => applyTemplate(tpl)}
                    className="px-2.5 py-1 text-xs font-medium border border-gray-200 rounded-full text-gray-700 bg-gray-50 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-800 transition-colors"
                    data-testid={`qc-return-template-${tpl.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {tpl.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Correction Notes */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Correction Notes <span className="text-red-500">*</span>
              <span className="text-gray-400 font-normal ml-1">(what the processor must fix)</span>
            </label>
            <textarea
              value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Describe specifically what needs to be corrected..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 resize-none"
              rows={3} maxLength={1000}
              data-testid="qc-return-notes"
            />
            <div className="flex justify-between mt-0.5">
              {error ? <p className="text-xs text-red-600">{error}</p> : <span />}
              <span className="text-[11px] text-gray-400">{notes.length}/1000</span>
            </div>
          </div>

          {/* Evidence */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Evidence <span className="text-gray-400 font-normal">
                (optional, max {MAX_FILES} {MAX_FILES === 1 ? 'file' : 'files'} / {MAX_SIZE_LABEL}{MAX_FILES === 1 ? '' : ' each'})
              </span>
            </label>
            <input
              ref={fileInputRef} type="file" multiple={MAX_FILES > 1} accept=".png,.jpg,.jpeg,.pdf,.txt,.docx"
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden" data-testid="qc-return-evidence-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || evidence.length >= MAX_FILES}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-xs text-gray-600 hover:border-amber-400 hover:bg-amber-50 disabled:opacity-50"
              data-testid="qc-return-evidence-add"
            >
              <Upload className="w-3.5 h-3.5" />
              {uploading
                ? 'Uploading…'
                : evidence.length === 0
                  ? `Add evidence ${MAX_FILES === 1 ? 'file' : 'files'} (screenshots, marked-up PDFs)`
                  : MAX_FILES === 1
                    ? 'Evidence file attached (1/1)'
                    : `Add more (${evidence.length}/${MAX_FILES})`}
            </button>
            {evidence.length > 0 && (
              <div className="mt-1.5 space-y-1">
                {evidence.map(ev => (
                  <div
                    key={ev.file_id}
                    className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1 text-xs"
                    data-testid={`qc-return-evidence-${ev.file_id}`}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Paperclip className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span className="truncate text-gray-800">{ev.filename}</span>
                      <span className="text-[10px] text-gray-400 flex-shrink-0">{(ev.size / 1024).toFixed(0)} KB</span>
                    </div>
                    <button
                      type="button" onClick={() => removeEvidence(ev.file_id)}
                      className="text-gray-400 hover:text-red-600 ml-2 flex-shrink-0"
                      aria-label="Remove evidence"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Sticky footer ───────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <Button
            variant="outline" onClick={onClose}
            disabled={isSubmitting} data-testid="qc-return-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting || !isValid}
            className="bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-2"
            data-testid="qc-return-confirm"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Returning...
              </>
            ) : (
              <>
                <RotateCcw className="w-4 h-4" />
                Return for Correction
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QCReturnModal;
